import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BookHeart, HeartHandshake, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const timelineIcons = [HeartHandshake, BookHeart, Sparkles];

const escapeXml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const wrapTextByWidth = (text: string, maxWidth: number, font: string, maxLines?: number) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    return text.split(/\s+/).filter(Boolean);
  }

  context.font = font;
  const words = text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (context.measureText(next).width <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  });

  if (current) lines.push(current);
  if (!maxLines || lines.length <= maxLines) return lines;

  const limited = lines.slice(0, maxLines);
  const last = limited[maxLines - 1];
  limited[maxLines - 1] = last.length > 2 ? `${last.slice(0, -1)}…` : `${last}…`;
  return limited;
};

const imageToDataUrl = async (path: string) => {
  try {
    const response = await fetch(path);
    if (!response.ok) return "";
    const blob = await response.blob();
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ""));
      reader.readAsDataURL(blob);
    });
  } catch {
    return "";
  }
};

const downloadFromBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const Therapists = () => {
  const { t } = useTranslation();
  const [exportingId, setExportingId] = useState<string | null>(null);
  const therapistIds = ["josef"];
  const timelineKeys = ["step1", "step2", "step3"];
  const careerTimelineKeys = ["entry1", "entry2", "entry3", "entry4", "entry5", "entry6", "entry7"];

  const exportTherapistCard = async (id: string) => {
    setExportingId(id);

    try {
      const name = t(`therapistsPage.profiles.${id}.name`);
      const role = t(`therapistsPage.profiles.${id}.role`);
      const bio = t(`therapistsPage.profiles.${id}.bio`);
      const intro = t(`therapistsPage.profiles.${id}.intro`);
      const photoPath = t(`therapistsPage.profiles.${id}.image`);
      const logoPath = "/images/omsorgsterapi-logo.png";

      const careerEntries = careerTimelineKeys.map((careerKey) => ({
        period: t(`therapistsPage.profiles.${id}.careerTimeline.${careerKey}.period`),
        title: t(`therapistsPage.profiles.${id}.careerTimeline.${careerKey}.title`),
        url: t(`therapistsPage.profiles.${id}.careerTimeline.${careerKey}.url`, { defaultValue: "" }),
      }));

      const focusEntries = timelineKeys.map((timelineKey) => ({
        period: t(`therapistsPage.profiles.${id}.timeline.${timelineKey}.period`),
        title: t(`therapistsPage.profiles.${id}.timeline.${timelineKey}.title`),
        description: t(`therapistsPage.profiles.${id}.timeline.${timelineKey}.description`),
      }));

      const [photoData, logoData] = await Promise.all([
        imageToDataUrl(photoPath),
        imageToDataUrl(logoPath),
      ]);

      const introFont = "italic 34px Arial, sans-serif";
      const bodyFont = "32px Arial, sans-serif";
      const lineMaxWidth = 1020;

      const introLines = wrapTextByWidth(intro, lineMaxWidth, introFont);
      const bioLines = wrapTextByWidth(bio, lineMaxWidth, bodyFont);

      const svgWidth = 1240;
      const sectionLeft = 90;
      const bodyLineHeight = 40;
      const introLineHeight = 40;

      const introBoxY = 348;
      const introTextY = introBoxY + 58;
      const introBoxHeight = Math.max(180, introLines.length * introLineHeight + 58);

      const bioHeadingY = introBoxY + introBoxHeight + 60;
      const bioStartY = bioHeadingY + 48;
      const bioEndY = bioStartY + bioLines.length * bodyLineHeight;

      const careerHeadingY = bioEndY + 64;
      const careerStartY = careerHeadingY + 32;
      const careerDotX = sectionLeft + 12;
      const careerTextX = sectionLeft + 44;

      const careerBlocks = careerEntries.map((entry) => {
        const titleLines = wrapTextByWidth(entry.title, 930, bodyFont, 3);
        const blockHeight = Math.max(96, 58 + (titleLines.length - 1) * 34);
        return { ...entry, titleLines, blockHeight };
      });

      let careerCursor = careerStartY;
      const careerParts: string[] = [];
      let firstDotY = careerStartY + 18;
      let lastDotY = careerStartY + 18;

      careerBlocks.forEach((block, index) => {
        const dotY = careerCursor + 18;
        if (index === 0) firstDotY = dotY;
        lastDotY = dotY;

        const titleTspans = block.titleLines
          .map((line, lineIndex) => `<tspan x="${careerTextX}" y="${careerCursor + 58 + lineIndex * 34}">${escapeXml(line)}</tspan>`)
          .join("");

        careerParts.push(`
          <circle cx="${careerDotX}" cy="${dotY}" r="8" fill="#006B5F" />
          <text x="${careerTextX}" y="${careerCursor + 22}" font-family="Arial, sans-serif" font-size="23" font-weight="700" letter-spacing="1" fill="#0A766B">${escapeXml(block.period.toUpperCase())}</text>
          <text x="${careerTextX}" y="${careerCursor + 58}" font-family="Arial, sans-serif" font-size="32" fill="#22353A">${titleTspans}</text>
        `);

        careerCursor += block.blockHeight + 18;
      });

      const focusHeadingY = careerCursor + 32;
      const focusStartY = focusHeadingY + 36;

      const focusBlocks = focusEntries.map((entry) => {
        const titleLines = wrapTextByWidth(entry.title, 960, "700 30px Arial, sans-serif", 2);
        const descriptionLines = wrapTextByWidth(entry.description, 960, "30px Arial, sans-serif");
        const titleHeight = titleLines.length * 34;
        const descHeight = descriptionLines.length * 34;
        const blockHeight = 34 + titleHeight + 16 + descHeight + 30;
        return { ...entry, titleLines, descriptionLines, blockHeight };
      });

      let focusCursor = focusStartY;
      const focusParts: string[] = [];
      focusBlocks.forEach((block) => {
        const titleTspans = block.titleLines
          .map((line, index) => `<tspan x="${sectionLeft + 28}" y="${focusCursor + 72 + index * 34}">${escapeXml(line)}</tspan>`)
          .join("");
        const descStartY = focusCursor + 72 + block.titleLines.length * 34 + 8;
        const descTspans = block.descriptionLines
          .map((line, index) => `<tspan x="${sectionLeft + 28}" y="${descStartY + index * 34}">${escapeXml(line)}</tspan>`)
          .join("");

        focusParts.push(`
          <rect x="${sectionLeft}" y="${focusCursor}" width="${svgWidth - 180}" height="${block.blockHeight}" rx="18" fill="#FFFFFF" stroke="#DCEDE9" />
          <text x="${sectionLeft + 28}" y="${focusCursor + 40}" font-family="Arial, sans-serif" font-size="23" font-weight="700" letter-spacing="1" fill="#0A766B">${escapeXml(block.period.toUpperCase())}</text>
          <text x="${sectionLeft + 28}" y="${focusCursor + 72}" font-family="Arial, sans-serif" font-size="31" font-weight="700" fill="#0D1F25">${titleTspans}</text>
          <text x="${sectionLeft + 28}" y="${descStartY}" font-family="Arial, sans-serif" font-size="30" fill="#37474F">${descTspans}</text>
        `);

        focusCursor += block.blockHeight + 16;
      });

      const estimatedContentEnd = focusCursor + 80;
      const svgHeight = Math.max(1850, estimatedContentEnd + 90);

      const introTspans = introLines
        .map((line, index) => `<tspan x="${sectionLeft}" y="${introTextY + index * introLineHeight}">${escapeXml(line)}</tspan>`)
        .join("");

      const bioTspans = bioLines
        .map((line, index) => `<tspan x="${sectionLeft}" y="${bioStartY + index * 34}">${escapeXml(line)}</tspan>`)
        .join("");

      const photoImage = photoData
        ? `<image href="${photoData}" x="90" y="155" width="150" height="150" clip-path="url(#avatarClip)" preserveAspectRatio="xMidYMid slice" />`
        : "";
      const logoImage = logoData
        ? `<image href="${logoData}" x="1050" y="78" width="90" height="90" preserveAspectRatio="xMidYMid meet" />`
        : "";

      const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#F7FBFA" />
      <stop offset="100%" stop-color="#EAF4F2" />
    </linearGradient>
    <linearGradient id="header" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#006B5F" />
      <stop offset="100%" stop-color="#0A8375" />
    </linearGradient>
    <clipPath id="avatarClip">
      <circle cx="165" cy="230" r="75" />
    </clipPath>
  </defs>
  <rect width="${svgWidth}" height="${svgHeight}" fill="url(#bg)" />
  <rect x="55" y="55" width="${svgWidth - 110}" height="${svgHeight - 110}" rx="34" fill="#FFFFFF" stroke="#D4E7E3" stroke-width="2" />
  <rect x="55" y="55" width="${svgWidth - 110}" height="120" rx="34" fill="url(#header)" />
  <text x="90" y="130" font-family="Georgia, 'Times New Roman', serif" font-size="46" font-weight="700" fill="#FFFFFF">Omsorgsterapi.dk</text>
  ${logoImage}
  <rect x="90" y="340" width="${svgWidth - 180}" height="${svgHeight - 460}" rx="24" fill="#F8FCFB" stroke="#DCEDE9" />
  <circle cx="165" cy="230" r="82" fill="#FFFFFF" stroke="#D5E7E2" stroke-width="4" />
  ${photoImage}
  <text x="275" y="220" font-family="Arial, sans-serif" font-size="52" font-weight="700" fill="#0B3D37">${escapeXml(name)}</text>
  <text x="275" y="270" font-family="Arial, sans-serif" font-size="27" font-weight="700" fill="#0A766B">${escapeXml(role)}</text>
  <line x1="90" y1="320" x2="${svgWidth - 90}" y2="320" stroke="#D8EAE6" stroke-width="2" />

  <rect x="90" y="${introBoxY}" width="${svgWidth - 180}" height="${introBoxHeight}" rx="18" fill="#E8F5F2" />
  <text x="${sectionLeft}" y="${introTextY - 30}" font-family="Arial, sans-serif" font-size="34" font-style="italic" fill="#154D46">${introTspans}</text>

  <text x="${sectionLeft}" y="${bioHeadingY}" font-family="Arial, sans-serif" font-size="46" font-weight="700" fill="#006B5F">${escapeXml(t("therapistsPage.export.bioHeading"))}</text>
  <text x="${sectionLeft}" y="${bioStartY}" font-family="Arial, sans-serif" font-size="32" fill="#22353A">${bioTspans}</text>

  <text x="${sectionLeft}" y="${careerHeadingY}" font-family="Arial, sans-serif" font-size="46" font-weight="700" fill="#006B5F">${escapeXml(t("therapistsPage.export.careerHeading"))}</text>
  <line x1="${careerDotX}" y1="${firstDotY}" x2="${careerDotX}" y2="${lastDotY}" stroke="#9DCFC8" stroke-width="3" />
  ${careerParts.join("")}

  <text x="${sectionLeft}" y="${focusHeadingY}" font-family="Arial, sans-serif" font-size="46" font-weight="700" fill="#006B5F">${escapeXml(t("therapistsPage.export.focusHeading"))}</text>
  ${focusParts.join("")}

  <text x="90" y="${svgHeight - 95}" font-family="Arial, sans-serif" font-size="24" fill="#4E6764">${escapeXml(t("therapistsPage.export.footer"))}</text>
</svg>`;

      const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
      const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = svgDataUrl;
      });

      const canvas = document.createElement("canvas");
      canvas.width = svgWidth;
      canvas.height = svgHeight;
      const context = canvas.getContext("2d");

      if (!context) {
        downloadFromBlob(svgBlob, `${name.replace(/\s+/g, "-").toLowerCase()}-bio-card.svg`);
        return;
      }

      context.drawImage(image, 0, 0);

      const pngBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
      if (pngBlob) {
        downloadFromBlob(pngBlob, `${name.replace(/\s+/g, "-").toLowerCase()}-bio-card.png`);
      } else {
        downloadFromBlob(svgBlob, `${name.replace(/\s+/g, "-").toLowerCase()}-bio-card.svg`);
      }
    } catch (error) {
      console.error("Failed to export therapist card", error);
    } finally {
      setExportingId(null);
    }
  };

  return (
    <section id="therapists" className="relative overflow-hidden py-12 sm:py-16 bg-slate-50">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-28 left-1/3 h-72 w-72 rounded-full bg-[#006B5F]/10 blur-3xl" />
        <div className="absolute -bottom-20 right-0 h-64 w-64 rounded-full bg-amber-200/40 blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <p className="mb-3 inline-flex items-center rounded-full border border-[#006B5F]/20 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-widest text-[#006B5F]">
            {t("therapistsPage.badge")}
          </p>
          <h2 className="mb-4 text-3xl font-bold text-[#006B5F] sm:text-4xl">
            {t("therapistsPage.title")}
          </h2>
          <p className="text-lg text-gray-700">{t("therapistsPage.subtitle")}</p>
        </div>

        <div className="space-y-10">
          {therapistIds.map((id) => (
            <article
              key={id}
              className="mx-auto max-w-6xl rounded-2xl border border-[#006B5F]/10 bg-white/95 p-5 shadow-lg shadow-[#006B5F]/10 sm:p-8"
            >
              <div className="mb-5 flex justify-end">
                <Button
                  onClick={() => void exportTherapistCard(id)}
                  disabled={exportingId === id}
                  className="bg-[#006B5F] text-white hover:bg-[#005a50]"
                >
                  {exportingId === id ? t("therapistsPage.export.exportingButton") : t("therapistsPage.export.button")}
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
                <div className="lg:col-span-2">
                  <div className="sticky top-8 rounded-2xl border border-gray-100 bg-gradient-to-b from-[#E9F6F4] to-white p-6">
                    <img
                      src={t(`therapistsPage.profiles.${id}.image`)}
                      alt={t(`therapistsPage.profiles.${id}.name`)}
                      className="mb-5 h-24 w-24 rounded-full border-4 border-white object-cover shadow-md"
                    />
                    <h3 className="text-2xl font-bold text-[#006B5F]">
                      {t(`therapistsPage.profiles.${id}.name`)}
                    </h3>
                    <p className="mb-4 text-sm font-semibold uppercase tracking-wide text-[#006B5F]/80">
                      {t(`therapistsPage.profiles.${id}.role`)}
                    </p>
                    <p className="leading-relaxed text-gray-700">
                      {t(`therapistsPage.profiles.${id}.bio`)}
                    </p>

                    <div className="mt-7 border-t border-[#006B5F]/15 pt-5">
                      <h4 className="mb-4 text-sm font-bold uppercase tracking-[0.16em] text-[#006B5F]">
                        {t(`therapistsPage.profiles.${id}.careerTimeline.title`)}
                      </h4>
                      <div className="relative ml-1 space-y-4 border-l-2 border-[#006B5F]/25 pl-5">
                        {careerTimelineKeys.map((careerKey) => (
                          <div key={careerKey} className="relative">
                            <div className="absolute -left-[1.46rem] top-1.5 h-2.5 w-2.5 rounded-full bg-[#006B5F]" />
                            <p className="text-xs font-semibold uppercase tracking-wide text-[#006B5F]/85">
                              {t(`therapistsPage.profiles.${id}.careerTimeline.${careerKey}.period`)}
                            </p>
                            {(() => {
                              const title = t(`therapistsPage.profiles.${id}.careerTimeline.${careerKey}.title`);
                              const url = t(`therapistsPage.profiles.${id}.careerTimeline.${careerKey}.url`, { defaultValue: "" });

                              if (url) {
                                return (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm leading-relaxed text-gray-800 underline decoration-[#006B5F]/40 underline-offset-4 hover:text-[#006B5F]"
                                  >
                                    {title}
                                  </a>
                                );
                              }

                              return (
                                <p className="text-sm leading-relaxed text-gray-800">
                                  {title}
                                </p>
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <p className="mb-6 rounded-xl border-l-4 border-[#006B5F] bg-[#006B5F]/5 p-4 text-base italic leading-relaxed text-gray-700">
                    {t(`therapistsPage.profiles.${id}.intro`)}
                  </p>

                  <div className="space-y-5">
                    {timelineKeys.map((timelineKey, index) => {
                      const Icon = timelineIcons[index % timelineIcons.length];

                      return (
                        <div
                          key={timelineKey}
                          className="relative rounded-xl border border-gray-100 bg-white p-5 shadow-sm"
                        >
                          <div className="absolute left-[-9px] top-7 h-4 w-4 rounded-full bg-[#006B5F]" />
                          <div className="mb-3 flex items-center gap-3">
                            <div className="rounded-lg bg-[#006B5F]/10 p-2 text-[#006B5F]">
                              <Icon className="h-4 w-4" />
                            </div>
                            <p className="text-sm font-semibold uppercase tracking-wider text-[#006B5F]/80">
                              {t(`therapistsPage.profiles.${id}.timeline.${timelineKey}.period`)}
                            </p>
                          </div>
                          <h4 className="mb-2 text-lg font-semibold text-slate-900">
                            {t(`therapistsPage.profiles.${id}.timeline.${timelineKey}.title`)}
                          </h4>
                          <p className="leading-relaxed text-gray-700">
                            {t(`therapistsPage.profiles.${id}.timeline.${timelineKey}.description`)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </article>
          ))}

          <div className="mx-auto max-w-6xl rounded-2xl border border-dashed border-[#006B5F]/30 bg-white/80 p-6 text-center">
            <h3 className="mb-2 text-xl font-semibold text-[#006B5F]">
              {t("therapistsPage.coming.title")}
            </h3>
            <p className="mx-auto max-w-2xl text-gray-700">
              {t("therapistsPage.coming.description")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Therapists;
